import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useWebhook } from '../hooks/useWebhook';
import { ContactEventData, ContactEventSchema } from '../types';

interface ContactFormProps {
  onSubmit?: (data: ContactEventData) => void;
  onSuccess?: (response: any) => void;
  onError?: (error: Error) => void;
  className?: string;
  submitButtonText?: string;
  loadingText?: string;
}

export const ContactForm: React.FC<ContactFormProps> = ({
  onSubmit,
  onSuccess,
  onError,
  className = '',
  submitButtonText = '전송하기',
  loadingText = '전송 중...'
}) => {
  const { sendContactEvent, isLoading, error } = useWebhook({
    webhookType: 'contact',
    onSuccess,
    onError,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactEventData>({
    resolver: zodResolver(ContactEventSchema),
  });

  const onSubmitForm = async (data: ContactEventData) => {
    try {
      const response = await sendContactEvent(data);
      if (response.success) {
        reset();
        onSubmit?.(data);
      }
    } catch (error) {
      console.error('Contact form submission failed:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmitForm)} className={className}>
      <div className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            이름 *
          </label>
          <input
            {...register('name')}
            type="text"
            id="name"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder="이름을 입력하세요"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            이메일 *
          </label>
          <input
            {...register('email')}
            type="email"
            id="email"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder="이메일을 입력하세요"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
            제목
          </label>
          <input
            {...register('subject')}
            type="text"
            id="subject"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder="제목을 입력하세요"
          />
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700">
            메시지 *
          </label>
          <textarea
            {...register('message')}
            id="message"
            rows={4}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder="메시지를 입력하세요"
          />
          {errors.message && (
            <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>
          )}
        </div>

        {error && (
          <div className="text-sm text-red-600">
            {error.message}
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-blue-500 hover:bg-blue-700 disabled:bg-blue-300 text-white font-bold py-2 px-4 rounded"
        >
          {isLoading ? loadingText : submitButtonText}
        </button>
      </div>
    </form>
  );
}; 